import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Paper,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloudIcon from '@material-ui/icons/Cloud';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { Header, Page, Content } from '@backstage/core-components';

const POLL_IDLE_MS = 10_000;
const POLL_ACTIVE_MS = 3_000;

const LAST_RUN_LABELS: Record<string, string> = {
  in_progress: 'In progress',
  queued: 'Queued',
  completed: 'Completed',
  success: 'Success',
  failure: 'Failed',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
  timed_out: 'Timed out',
  action_required: 'Action required',
  neutral: 'Neutral',
  startup_failure: 'Startup failure',
  stale: 'Stale',
};

type State = 'live' | 'suspended' | 'unknown' | 'never';
type Progress =
  | 'idle'
  | 'provisioning'
  | 'pausing'
  | 'resuming'
  | 'destroying';

interface PreviewEnv {
  slug: string;
  state: State;
  progress: Progress;
  branch_url: string;
  last_run?: {
    id: number;
    status: string;
    conclusion: string | null;
    url: string;
    name: string;
    updated_at: string;
  };
}

const PREVIEW_PREFIXES = ['team-product', 'team-design'] as const;
type PreviewPrefix = (typeof PREVIEW_PREFIXES)[number];

// Mirrors sanitizeSlug() in the backend / preview-env.yml: DNS-safe, ≤30 chars.
const sanitizeSlug = (raw: string) =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/g, '');

const useStyles = makeStyles((theme) => ({
  table: { '& th': { fontWeight: 600 } },
  buildSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  buildRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  branchField: { minWidth: 280, flex: '1 1 280px' },
  prefixField: { minWidth: 150 },
  slugField: { minWidth: 200 },
  stateLive: { backgroundColor: '#2e7d32', color: '#fff' },
  stateSuspended: { backgroundColor: '#757575', color: '#fff' },
  stateNever: { backgroundColor: '#1976d2', color: '#fff' },
  stateUnknown: { backgroundColor: '#ed6c02', color: '#fff' },
  progressChip: { animation: '$pulse 1.5s ease-in-out infinite' },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.6 },
  },
  actions: { display: 'flex', gap: theme.spacing(1), justifyContent: 'flex-end', alignItems: 'center' },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}));

const stateLabel = (s: State) =>
  s === 'live'
    ? 'Live'
    : s === 'suspended'
      ? 'Suspended'
      : s === 'never'
        ? 'Never provisioned'
        : 'Unknown';

const progressLabel = (p: Progress) =>
  p === 'provisioning'
    ? 'Provisioning…'
    : p === 'pausing'
      ? 'Pausing…'
      : p === 'resuming'
        ? 'Resuming…'
        : p === 'destroying'
          ? 'Destroying…'
          : '';

export const PreviewEnvPage = () => {
  const classes = useStyles();
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [envs, setEnvs] = useState<PreviewEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  // Queueing a Gary handoff run costs money and the box's single slot, so it
  // asks first and then says where the run went.
  const [confirmRun, setConfirmRun] = useState<string | null>(null);
  const [queued, setQueued] = useState<{ slug: string; id: number } | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Build-from-branch form state ──────────────────────────────────────
  const [sourceBranches, setSourceBranches] = useState<string[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [prefix, setPrefix] = useState<PreviewPrefix>('team-product');
  const [slug, setSlug] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const [building, setBuilding] = useState(false);

  const fetchEnvs = useCallback(async () => {
    try {
      const base = await discovery.getBaseUrl('preview-envs');
      const res = await fetchApi.fetch(`${base}/preview-envs`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = (await res.json()) as { envs: PreviewEnv[] };
      setEnvs(
        data.envs.sort((a, b) => a.slug.localeCompare(b.slug)),
      );
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [discovery, fetchApi]);

  // Polling — fast when any row is busy, slow otherwise.
  useEffect(() => {
    fetchEnvs();
    const schedule = () => {
      const anyActive =
        envs.some(e => e.progress !== 'idle') ||
        Object.values(busy).some(Boolean);
      pollTimer.current = setTimeout(
        async () => {
          await fetchEnvs();
          schedule();
        },
        anyActive ? POLL_ACTIVE_MS : POLL_IDLE_MS,
      );
    };
    schedule();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envs.length, JSON.stringify(envs.map(e => e.progress)), JSON.stringify(busy)]);

  // Load the list of branches you can build a preview env from.
  useEffect(() => {
    (async () => {
      try {
        const base = await discovery.getBaseUrl('preview-envs');
        const res = await fetchApi.fetch(`${base}/preview-envs/source-branches`);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = (await res.json()) as { branches: string[] };
        setSourceBranches(data.branches ?? []);
      } catch {
        // Non-fatal: the form just won't have suggestions.
      }
    })();
  }, [discovery, fetchApi]);

  // Slug auto-derives from the branch name until the user edits it.
  const onSelectBranch = (branch: string | null) => {
    setSelectedBranch(branch);
    if (branch && !slugDirty) {
      setSlug(sanitizeSlug(branch.split('/').pop() ?? branch));
    }
  };

  const cleanSlug = sanitizeSlug(slug);

  const onBuild = async () => {
    if (!selectedBranch || !cleanSlug) return;
    setBuilding(true);
    setError(null);
    try {
      const base = await discovery.getBaseUrl('preview-envs');
      const res = await fetchApi.fetch(`${base}/preview-envs/build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceBranch: selectedBranch, prefix, slug: cleanSlug }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status}: ${body}`);
      }
      setSelectedBranch(null);
      setSlug('');
      setSlugDirty(false);
      await fetchEnvs();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBuilding(false);
    }
  };

  const callAction = async (
    slug: string,
    method: 'POST' | 'DELETE',
    path: string,
  ) => {
    setBusy(b => ({ ...b, [slug]: true }));
    try {
      const base = await discovery.getBaseUrl('preview-envs');
      const res = await fetchApi.fetch(`${base}${path}`, { method });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status}: ${body}`);
      }
      await fetchEnvs();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(b => ({ ...b, [slug]: false }));
    }
  };

  // Gary lives behind the ai-qa plugin, which owns the box's token.
  const onRunGary = async () => {
    const slug = confirmRun;
    if (!slug) return;
    setConfirmRun(null);
    setBusy(b => ({ ...b, [slug]: true }));
    try {
      const base = await discovery.getBaseUrl('ai-qa');
      const res = await fetchApi.fetch(`${base}/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sandbox: slug }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? res.statusText);
      setQueued({ slug, id: body.id });
    } catch (err: any) {
      setError(`Gary run for ${slug}: ${err.message}`);
    } finally {
      setBusy(b => ({ ...b, [slug]: false }));
    }
  };

  const onPause = (slug: string) =>
    callAction(slug, 'POST', `/preview-envs/${slug}/pause`);
  const onResume = (slug: string) =>
    callAction(slug, 'POST', `/preview-envs/${slug}/resume`);
  const onDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    const slug = confirmDelete;
    setConfirmDelete(null);
    await callAction(slug, 'DELETE', `/preview-envs/${slug}`);
  };

  const renderState = (env: PreviewEnv) => {
    if (env.progress !== 'idle') {
      return (
        <Chip
          size="small"
          className={classes.progressChip}
          label={progressLabel(env.progress)}
        />
      );
    }
    const cls =
      env.state === 'live'
        ? classes.stateLive
        : env.state === 'suspended'
          ? classes.stateSuspended
          : env.state === 'never'
            ? classes.stateNever
            : classes.stateUnknown;
    return <Chip size="small" className={cls} label={stateLabel(env.state)} />;
  };

  const renderActions = (env: PreviewEnv) => {
    const disabled = env.progress !== 'idle' || !!busy[env.slug];
    return (
      <Box className={classes.actions}>
        {env.state === 'live' && (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() => onPause(env.slug)}
          >
            Pause
          </Button>
        )}
        {(env.state === 'suspended' || env.state === 'never') && (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || env.state === 'never'}
            onClick={() => onResume(env.slug)}
          >
            Resume
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          // A handoff run drives the sandbox in a browser — it needs the
          // environment actually up.
          disabled={disabled || env.state !== 'live'}
          onClick={() => setConfirmRun(env.slug)}
        >
          Run Gary
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          disabled={disabled}
          onClick={() => setConfirmDelete(env.slug)}
        >
          Delete
        </Button>
        {busy[env.slug] && <CircularProgress size={20} />}
      </Box>
    );
  };

  return (
    <Page themeId="tool">
      <Header
        title="Preview Environments"
        subtitle="Sandbox cluster — team-product/* and team-design/* branches"
      >
        <CloudIcon />
      </Header>
      <Content>
        <Paper className={classes.buildSection} variant="outlined">
          <Typography variant="h6" gutterBottom>
            Build from a branch
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Pick any branch — this creates a{' '}
            <code>{prefix}/{cleanSlug || '<slug>'}</code> branch at its current
            HEAD, which builds and deploys a preview env.
          </Typography>
          <Box className={classes.buildRow} mt={2}>
            <Autocomplete
              className={classes.branchField}
              options={sourceBranches}
              value={selectedBranch}
              onChange={(_e, v) => onSelectBranch(v)}
              disabled={building}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Source branch"
                  variant="outlined"
                  size="small"
                />
              )}
            />
            <TextField
              className={classes.prefixField}
              select
              label="Prefix"
              value={prefix}
              onChange={e => setPrefix(e.target.value as PreviewPrefix)}
              variant="outlined"
              size="small"
              disabled={building}
            >
              {PREVIEW_PREFIXES.map(p => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.slugField}
              label="Slug"
              value={slug}
              onChange={e => {
                setSlug(e.target.value);
                setSlugDirty(true);
              }}
              variant="outlined"
              size="small"
              disabled={building}
              error={!!slug && !cleanSlug}
              helperText={
                cleanSlug
                  ? `https://${cleanSlug}.sandbox.salestrak.io`
                  : 'DNS-safe, ≤30 chars'
              }
            />
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedBranch || !cleanSlug || building}
              onClick={onBuild}
              style={{ marginTop: 4 }}
            >
              Build
            </Button>
            {building && <CircularProgress size={24} style={{ marginTop: 8 }} />}
          </Box>
        </Paper>
        {loading && <CircularProgress />}
        {error && (
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
        )}
        {!loading && envs.length === 0 && (
          <Box className={classes.empty}>
            <Typography variant="h6">No preview environments yet.</Typography>
            <Typography variant="body2">
              Push a branch named <code>team-product/&lt;slug&gt;</code> or{' '}
              <code>team-design/&lt;slug&gt;</code> to create one.
            </Typography>
          </Box>
        )}
        {!loading && envs.length > 0 && (
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Slug</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {envs.map(env => (
                <TableRow key={env.slug}>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <MuiLink
                        href={env.branch_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {env.slug}
                      </MuiLink>
                      <Tooltip title={`Open ${env.slug}.sandbox.salestrak.io`}>
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://${env.slug}.sandbox.salestrak.io`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon style={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{renderState(env)}</TableCell>
                  <TableCell>
                    {env.last_run ? (
                      <MuiLink
                        href={env.last_run.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {LAST_RUN_LABELS[
                          env.last_run.status === 'completed' && env.last_run.conclusion
                            ? env.last_run.conclusion
                            : env.last_run.status
                        ] ?? env.last_run.status}
                      </MuiLink>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">{renderActions(env)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Dialog
          open={confirmDelete !== null}
          onClose={() => setConfirmDelete(null)}
        >
          <DialogTitle>Delete preview env?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will delete branch{' '}
              <code>team-product/{confirmDelete}</code> on GitHub and trigger
              the destroy workflow. The cluster namespace, ECR images, and DNS
              records will be removed. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              onClick={onDeleteConfirmed}
              variant="contained"
              color="secondary"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={!!confirmRun} onClose={() => setConfirmRun(null)}>
          <DialogTitle>Run Gary against {confirmRun}?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Queues a handoff run comparing{' '}
              <code>{confirmRun}.sandbox.salestrak.io</code> against{' '}
              <code>dev.salestrak.io</code> on the runner box. The box runs one
              job at a time, so it starts when the queue reaches it, and the run
              spends up to $5 of inference. Results land in Gary.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRun(null)}>Cancel</Button>
            <Button onClick={onRunGary} variant="contained" color="primary">
              Queue run
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={!!queued} onClose={() => setQueued(null)}>
          <DialogTitle>Queued job {queued?.id}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Gary will run against <code>{queued?.slug}</code> when the box
              reaches it. Watch it on{' '}
              <MuiLink
                href="https://gary.devops.salestrak.io"
                target="_blank"
                rel="noopener"
              >
                Gary's queue
              </MuiLink>
              , and the report appears on the Gary page when it finishes.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQueued(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};
